import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import EditScheduleModal from './EditScheduleModal';

interface EditScheduleModalContextType {
  isOpen: boolean;
  takerMarket: TakerMarket | null;
  openModal: (takerMarket: TakerMarket) => void;
  closeModal: () => void;
}

const EditScheduleModalContext = createContext<EditScheduleModalContextType | undefined>(undefined);

export const useEditScheduleModalContext = () => {
  const context = useContext(EditScheduleModalContext);

  if (!context) {
    throw new Error('useEditScheduleModalContext must be used within a EditScheduleModalProvider');
  }

  return context;
};

const EditScheduleModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [takerMarket, setTakerMarket] = useState<TakerMarket | null>(null);
  const { trackEnterpriseEvent } = useReporting();

  const clearTakerMarket = useCallback(() => {
    // delay clearing takerMarket to allow modal close animation
    setTimeout(() => {
      setTakerMarket(null);
    }, 300);
  }, []);

  const openModal = useCallback(
    (tm: TakerMarket) => {
      setTakerMarket(tm);
      setIsOpen(true);
      trackEnterpriseEvent('edit-schedule-modal::opened', {
        marketUuid: tm.marketUuid,
        takerDivisionId: tm.takerDivisionId,
      });
    },
    [trackEnterpriseEvent]
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    clearTakerMarket();
    trackEnterpriseEvent('edit-schedule-modal::closed');
  }, [clearTakerMarket, trackEnterpriseEvent]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      takerMarket,
      openModal,
      closeModal,
    }),
    [isOpen, takerMarket, openModal, closeModal]
  );

  return <EditScheduleModalContext.Provider value={contextValue}>{children}</EditScheduleModalContext.Provider>;
};

const EditScheduleModalComponent = () => {
  const { isOpen, takerMarket, closeModal } = useEditScheduleModalContext();

  return takerMarket ? <EditScheduleModal open={isOpen} takerMarket={takerMarket} onClose={closeModal} /> : null;
};

export const EditScheduleModalWithContext = ({ children }: { children: ReactNode }) => {
  return (
    <EditScheduleModalProvider>
      <EditScheduleModalComponent />
      {children}
    </EditScheduleModalProvider>
  );
};
